import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { GatewayRanking } from 'commonTypes/api/gatewayRanking';

import { getStatusesAPI } from 'shared/api/statuses';
import type { TOffer } from 'shared/types/TRankingService';

import { mapOffersWithStatuses } from '../lib/mapOffersWithStatuses';
import type { IOffersState } from '../types';

import { setPreApprovingOffersData, showHiddenOffersReducer } from './reducers';

const initialState: IOffersState = {
  calcId: '',
  offers: [],
  isLoading: true,
  isErrorResponse: false,
  isBentoRankingModel: false,
  offersStatuses: {},
  banksWithActiveStatuses: [],
  isAvailableMpl: undefined,
  availableBanks: [],
  hiddenOffers: [],
};

export const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    setOffers: (state, { payload }: PayloadAction<IOffersState & { activeGroupKey?: GatewayRanking.TGroupType }>) => {
      // подмешиваем статусы, пришедшие с сервера, в текущие статусы на клиенте,
      // так как loans не сразу возвращает новые статусы, а через 10-15 секунд
      // и если мы их не подмешаем, то при обновлении списка офферов
      // продукты будут отображаться без активной заявки
      const currentStatuses = {
        ...state.offersStatuses,
        ...payload.offersStatuses,
      };

      const { activeGroupKey = 'Active', offers = [] } = payload || {};

      const currentBanksWithActiveStatuses = Array.from(
        new Set([...state.banksWithActiveStatuses, ...payload.banksWithActiveStatuses]),
      );

      const mappedOffers = mapOffersWithStatuses({
        offers,
        currentStatuses,
        currentBanksWithActiveStatuses,
        activeGroupKey,
      });

      const seenActiveBanks = new Set<string>();
      state.offers = mappedOffers.filter((offer) => {
        if (offer.group !== activeGroupKey) {
          return true;
        }

        if (seenActiveBanks.has(offer.bankId)) {
          return false;
        }

        seenActiveBanks.add(offer.bankId);
        return true;
      });

      state.isLoading = false;
      state.calcId = payload.calcId;
      state.isBentoRankingModel = payload.isBentoRankingModel ?? false;
      state.offersStatuses = currentStatuses;
      state.banksWithActiveStatuses = currentBanksWithActiveStatuses;
      state.availableBanks = payload.availableBanks;
      state.hiddenOffers = payload.hiddenOffers;
    },
    addOffer: (state, { payload }: PayloadAction<TOffer>) => {
      const offerIndex = state.offers.findIndex((offer) => offer.id === payload.id);

      if (offerIndex === -1) {
        state.offers = [...state.offers, payload];
      } else {
        state.offers[offerIndex] = {
          ...state.offers[offerIndex],
          ...payload,
        };
      }
      state.isLoading = false;
    },
    updateOfferData: (state, { payload }: PayloadAction<{ id: string } & Partial<Omit<TOffer, 'id'>>>) => {
      const { id, ...offerData } = payload;

      state.offers = state.offers.map((offer) => {
        if (offer.id === id) {
          // если в обновлении данных есть статус, то добавляем его в offersStatuses
          // и добавляем банк в banksWithActiveStatuses, если его там нет
          if (offerData.status) {
            state.offersStatuses = {
              ...state.offersStatuses,
              [id]: offerData.status,
            };
            state.banksWithActiveStatuses = Array.from(new Set([...state.banksWithActiveStatuses, offer.bankId]));
          }

          return { ...offer, ...offerData };
        }
        return offer;
      });
      state.isLoading = false;
    },
    updateOffersStatuses: (
      state,
      {
        payload,
      }: PayloadAction<{
        productId: string;
        bankId: string;
        status: TOffer['status'];
      }>,
    ) => {
      const { productId, status, bankId } = payload;

      state.offersStatuses = {
        ...state.offersStatuses,
        [productId]: status,
      };
      state.banksWithActiveStatuses = Array.from(new Set([...state.banksWithActiveStatuses, bankId]));
    },
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },

    setIsAvailableMpl: (state, { payload }: PayloadAction<boolean | undefined>) => {
      state.isAvailableMpl = payload;
    },

    setIsErrorResponse: (state, { payload }: PayloadAction<boolean>) => {
      state.isErrorResponse = payload;
    },

    clearGroup: (state, { payload }: PayloadAction<TOffer['group']>) => {
      state.offers = state.offers.filter((offer) => offer.group !== payload);
    },

    disableOffersFromSameBank: (state, { payload }: PayloadAction<{ offerId: string; bankId: string }>) => {
      const { offerId, bankId } = payload;

      state.offers = state.offers.filter((offer) => {
        if (offer.bankId === bankId && offer.id !== offerId && !offer.additionalInfo?.isReferral) {
          return false;
        }
        return true;
      });
    },
    setAvailableBanks: (state, { payload }: PayloadAction<string[]>) => {
      return {
        ...state,
        availableBanks: payload,
      };
    },
    setPreApprovingOffers: setPreApprovingOffersData,
    resetOffersState: () => ({ ...initialState }),
    showHiddenOffers: showHiddenOffersReducer,
  },
  extraReducers: (builder) => {
    builder.addMatcher(getStatusesAPI.endpoints.getStatuses.matchFulfilled, (state, { payload }) => {
      if (Array.isArray(payload)) {
        const statusMap = new Map(payload.map((status) => [status.productId, status]));

        state.offers = state.offers.map((offer) => {
          if (offer.id && statusMap.has(offer.id)) {
            const statusInfo = statusMap.get(offer.id);
            if (statusInfo?.status) {
              // если при поллинге пришел новый статус, то добавляем его в offersStatuses
              // и добавляем банк в banksWithActiveStatuses, если его там нет
              state.offersStatuses = {
                ...state.offersStatuses,
                [offer.id]: statusInfo.status,
              };
              state.banksWithActiveStatuses = Array.from(new Set([...state.banksWithActiveStatuses, offer.bankId]));

              if (statusInfo?.offerView) {
                return {
                  ...offer,
                  ...statusInfo.offerView.groupItem,
                  bankId: statusInfo.offerView.organization?.id,
                  bankName: statusInfo.offerView.organization?.name,
                  logo: statusInfo.offerView.organization?.logo,
                  phones: statusInfo.offerView.organization?.phones,
                  status: statusInfo?.status,
                  summaries: statusInfo.offerView.groupItem?.summaries?.map((summary: any) => ({
                    label: summary?.text,
                    value: String(summary?.value),
                  })),
                };
              }

              return { ...offer, status: statusInfo?.status };
            }
          }
          return offer;
        });
      }
    });
    builder.addMatcher(getStatusesAPI.endpoints.getStatusesV2.matchFulfilled, setPreApprovingOffersData);
  },
});

export const {
  setOffers,
  setLoading,
  setIsAvailableMpl,
  addOffer,
  updateOfferData,
  clearGroup,
  disableOffersFromSameBank,
  setIsErrorResponse,
  setPreApprovingOffers,
  setAvailableBanks,
  resetOffersState,
  updateOffersStatuses,
  showHiddenOffers,
} = offersSlice.actions;
